// src/pages/TeamAnswers.js
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/backend/api/';


function TeamAnswers() {
  const navigate = useNavigate();

  const teamId = localStorage.getItem('teamId');
  const teamName = localStorage.getItem('teamName') || 'Onbekend team';
  const teamCode = localStorage.getItem('teamCode') || '';

  const [config, setConfig] = useState({ roundsTotal: 8, questionsPerRound: 8 });
  const [currentRound, setCurrentRound] = useState(1);
  const [roundStatus, setRoundStatus] = useState('inactive');
  const [answers, setAnswers] = useState([]);
  const [message, setMessage] = useState('Wachten op start van de quiz...');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Refs to avoid stale state inside setInterval / async callbacks
  const currentRoundRef = useRef(currentRound);
  const roundStatusRef = useRef(roundStatus);

  useEffect(() => {
    currentRoundRef.current = currentRound;
  }, [currentRound]);

  useEffect(() => {
    roundStatusRef.current = roundStatus;
  }, [roundStatus]);

  // Protect route
  useEffect(() => {
    if (!teamId) navigate('/');
  }, [teamId, navigate]);

  // Load existing answers for a round
  const loadAnswers = async (roundId, questionsPerRoundOverride) => {
    try {
      const res = await axios.get(
        `${API_URL}get_answers.php?team_id=${teamId}&round_id=${roundId}`,
        { params: { _: Date.now() } } // cache buster
      );

      if (res.data.success) {
        const qpr = questionsPerRoundOverride ?? config.questionsPerRound;
        const loaded = Array(qpr).fill('');

        (res.data.answers || []).forEach((text, index) => {
          if (index < loaded.length) loaded[index] = text || '';
        });

        setAnswers(loaded);
      }
    } catch (err) {
      console.error('Failed to load answers:', err);
    }
  };

  // Fetch quiz status and config
  const fetchQuizStatus = async (forceLoadAnswers = false) => {
    try {
const res = await axios.get(`${API_URL}get_quiz_status.php`, {
      params: {
        team_id: teamId,           // ← Voeg team_id toe
        _: Date.now()              // voorkomt cache
      },
      headers: { 
        'Cache-Control': 'no-cache' 
      }
    });

      if (!res.data.success) return;

      const roundsTotal = Number(res.data.rounds_total) || 8;
      const questionsPerRound = Number(res.data.questions_per_round) || 8;

      setConfig({ roundsTotal, questionsPerRound });

      const statuses = res.data.round_statuses || {};

      // 1) First ACTIVE (lowest number)
      let activeRound = null;
      for (let r = 1; r <= roundsTotal; r++) {
        if (statuses[String(r)] === 'active') {
          activeRound = r;
          break;
        }
      }

      // 2) If no active: latest FINISHED (highest number)
      let latestFinished = null;
      if (!activeRound) {
        for (let r = roundsTotal; r >= 1; r--) {
          if (statuses[String(r)] === 'finished') {
            latestFinished = r;
            break;
          }
        }
      }

      // 3) If no finished: latest SCORED (highest number) (optional but useful)
      let latestScored = null;
      if (!activeRound && !latestFinished) {
        for (let r = roundsTotal; r >= 1; r--) {
          if (statuses[String(r)] === 'scored') {
            latestScored = r;
            break;
          }
        }
      }

      // Choose round
      const targetRound = activeRound || latestFinished || latestScored || 1;
      const targetStatus = statuses[String(targetRound)] || 'inactive';

      // Compare against refs (latest UI state), not stale closure values
      const roundChanged = targetRound !== currentRoundRef.current;

      if (roundChanged || forceLoadAnswers) {
        setCurrentRound(targetRound);
        setRoundStatus(targetStatus);
        await loadAnswers(targetRound, questionsPerRound);
      } else if (targetStatus !== roundStatusRef.current) {
        setRoundStatus(targetStatus);
      }
    } catch (err) {
      console.error('Status fetch error:', err);
      setMessage('Kon status niet ophalen');
    } finally {
      setLoading(false);
    }
  };

  // Poll status
  useEffect(() => {
    fetchQuizStatus(true);

    const interval = setInterval(() => {
      fetchQuizStatus(false);
    }, 3000);

    return () => clearInterval(interval);
    // Intentionally empty deps: refs handle latest state safely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save (only when active)
  useEffect(() => {
    if (roundStatus !== 'active' || answers.every(a => a.trim() === '')) return;

    setSaving(true);
    const timer = setTimeout(() => {
      axios
        .post(`${API_URL}save_answers.php`, { 
          team_id: teamId,
          round_id: currentRound,
          answers: answers
        })
        .then(() => setSaving(false))
        .catch(() => setSaving(false));
    }, 1000);

    return () => clearTimeout(timer);
  }, [answers, roundStatus, teamId, currentRound]);

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  // Message
  useEffect(() => {
    if (roundStatus === 'active') {
      setMessage(
        <span>
          Ronde actief – vul jullie antwoorden in!
          {saving && <img src="/floppy-disk.png" alt="opslaan" className="inline ml-2 h-4 w-4" />}
        </span>
      );
    } else if (roundStatus === 'finished') {
      setMessage('Ronde afgesloten – wachten op nakijken...');
    } else if (roundStatus === 'scored') {
      setMessage('Ronde nagekeken – bekijk jullie antwoorden.');
    } else {
      setMessage('Wachten op start van de quiz...');
    }
  }, [roundStatus, saving]);

  if (loading) return <div className="p-8 text-center text-xl">Laden...</div>;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-center mb-2">{teamName}</h1>
        <p className="text-center text-gray-600 mb-8">
          Code: {teamCode} • Ronde {currentRound} van {config.roundsTotal}
        </p>

        <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4 mb-8 text-center">
          <p className="text-xl font-semibold">{message}</p>
        </div>

        <div className="space-y-4">
          {answers.map((answer, index) => (
            <div key={index} className="flex items-center gap-4">
              <label className="w-24 text-right font-medium text-lg"># {index + 1}</label>
              <input
                type="text"
                value={answer}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                disabled={roundStatus !== 'active'}
                className="flex-1 px-4 py-3 text-xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-200"
                placeholder="Antwoord..."
              />
            </div>
          ))}
        </div>
        <br /><br />
        <hr/>
        
          <p className="text-center text-gray-600 mb-8">
          Het opslaan van de antwoorden gaat automatisch
        </p>
      </div>
    </div>
  );
}

export default TeamAnswers;
